// Language context wired to /app/frontend/src/lib/translations.js
// Rules:
// - default lang stored in localStorage (`lp_lang`).
// - t(key, vars?) returns the string; if a key is missing in the active lang,
//   we log a dev-time warning and fall back to English (see build note below).
// - Build-time completeness: `yarn build` runs `node scripts/check-i18n.js`
//   which fails if any key is missing in either dictionary.
import React, { createContext, useContext, useEffect, useState } from "react";
import { DICTIONARIES } from "./translations";

const LangContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  formatCurrency: (n) => `$${n}`,
  formatNumber: (n) => String(n),
  formatDate: (d) => String(d),
});

const LOG_MISSING = process.env.NODE_ENV !== "production";

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{${k}}`));
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("lp_lang") || "en");

  useEffect(() => {
    localStorage.setItem("lp_lang", lang);
    // NOTE: We deliberately do NOT set `document.documentElement.lang = lang`.
    // Browser CSS `text-transform: uppercase` applies locale-specific casing
    // when the ancestor `lang` is Turkish: `i` → `İ`, turning
    // English UI chrome like "Description" into "DESCRİPTİON".
    // We keep `<html lang="en">` fixed and track the active app language
    // through a separate `data-lang` attribute for anything that needs it
    // (analytics, custom hooks, etc.). Translated user-facing strings still
    // render correctly regardless of the outer lang attribute.
    document.documentElement.setAttribute("data-lang", lang);
    document.documentElement.setAttribute("lang", "en");
  }, [lang]);

  const t = (k, vars) => {
    const dict = DICTIONARIES[lang] || DICTIONARIES.en;
    let val = dict[k];
    if (val === undefined) {
      if (LOG_MISSING) console.warn(`[i18n] missing key '${k}' in '${lang}'`);
      val = DICTIONARIES.en[k];
      if (val === undefined) return k;
    }
    return interpolate(val, vars);
  };

  const locale = lang === "tr" ? "tr-TR" : "en-US";
  const formatCurrency = (n, currency = "USD") => {
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 }).format(n);
    } catch {
      return `${currency} ${n}`;
    }
  };
  const formatNumber = (n) => {
    try { return new Intl.NumberFormat(locale).format(n); } catch { return String(n); }
  };
  const formatDate = (d, opts) => {
    try { return new Intl.DateTimeFormat(locale, opts).format(new Date(d)); } catch { return String(d); }
  };

  return (
    <LangContext.Provider value={{ lang, setLang: setLangState, t, formatCurrency, formatNumber, formatDate }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
